import React, { useContext } from 'react';
import { bool, string } from 'prop-types';
import classNames from 'classnames/bind';
import { Typography } from '@one-thd/sui-atomic-components';
import { StorePodContext } from '../StorePod.context';
import { StorePodLoader } from './StorePodLoader';

const cx = classNames.bind();

const convertTo12HrFormat = (hhmm) => {
  if (hhmm && hhmm.split(':').length && hhmm.split(':').length === 2) {
    const hoursStr = hhmm.split(':')[0];
    const minStr = hhmm.split(':')[1];

    const hoursInt = parseInt(hoursStr, 10);
    if (hoursInt === 0) {
      return `12${':'}${minStr}am`;
    }
    if (hoursInt < 12) {
      return `${hoursStr}${':'}${minStr}am`;
    }
    if (hoursInt === 12) {
      return `${hoursStr}${':'}${minStr}pm`;
    }

    const pmHoursString = (hoursInt - 12).toString();
    return `${pmHoursString}${':'}${minStr}pm`;
  }
  return hhmm;
};

const replaceClosedDayFromMainStore = (storeHoursInQuestion, mainStoreHours) => {
  const closedDays = {};

  if (mainStoreHours) {
    const kvps = Object.entries(mainStoreHours);
    kvps.forEach(([key, value]) => {
      if (!!value.open && value.open === value.close) {
        closedDays[key] = value;
      }
    });
  }

  return {
    ...storeHoursInQuestion,
    ...closedDays
  };
};

export const StorePodHours = ({
  hoursType, displayMultiline, boldLabel, height
}) => {
  const { isDesignStore, loading, store } = useContext(StorePodContext);

  const isCurbside = hoursType === 'curbside';
  const mainStoreHours = store?.storeHours;

  const setHours = () => {
    switch (hoursType) {
    case 'curbside':
      return store?.curbsidePickupHours;
    case 'proDesk':
      /* TODO: remove this wrapping function and return store?.proDeskHours directly
      once Store Search API adds the logic on their end */
      return replaceClosedDayFromMainStore(store?.proDeskHours, mainStoreHours);
    case 'rental':
      return replaceClosedDayFromMainStore(store?.toolRentalHours, mainStoreHours);
    default:
      return mainStoreHours;
    }
  };

  const hours = setHours();

  const {
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    sunday
  } = hours || {};

  const mon = { name: 'Monday', ...monday };
  const tue = { name: 'Tuesday', ...tuesday };
  const wed = { name: 'Wednesday', ...wednesday };
  const thu = { name: 'Thursday', ...thursday };
  const fri = { name: 'Friday', ...friday };
  const sat = { name: 'Saturday', ...saturday };
  const sun = { name: 'Sunday', ...sunday };

  let daysOfTheWeek = [mon, tue, wed, thu, fri, sat];

  const normalDays = [];
  const irregularDays = [];

  daysOfTheWeek.forEach((day) => {
    const hasIrregularHours = day?.open && day?.close && (
      day?.open !== mon?.open || day?.close !== mon?.close
    );
    if (hasIrregularHours) {
      irregularDays.push(day);
    } else if (irregularDays.length === 0) {
      normalDays.push(day);
    } else {
      irregularDays.push(day);
    }
  });

  const getOpenCloseHours = (day) => {
    if (day?.open === day?.close) {
      return 'Closed';
    }
    return `${convertTo12HrFormat(day?.open)} - ${convertTo12HrFormat(day?.close)}`;
  };

  const getOpenCloseLabel = (day, day2) => {
    if (isCurbside) {
      return 'Curbside: ';
    }
    if (day2) {
      return `${day?.name.substring(0, 3)}-${day2?.name.substring(0, 3)}: `;
    }
    return `${day?.name.substring(0, 3)}: `;
  };

  const labelStyles = cx('sui-font-capitalize', { 'sui-font-bold': boldLabel });

  const getOpenCloseBlock = (day, day2) => {
    return (
      <div className="sui-overflow-hidden sui-text-ellipsis" key={day?.storeId}>
        <Typography height={height}>
          <span className={labelStyles}>
            {getOpenCloseLabel(day, day2)}
          </span>
          {displayMultiline && (<br />)}
          {getOpenCloseHours(day)}
        </Typography>
      </div>
    );
  };

  if (isDesignStore || (isCurbside && !hours)) return null;

  let numberOfRows = isCurbside ? 1 : 2;
  if (displayMultiline) {
    numberOfRows *= 2;
  }

  if (loading) {
    return (
      <div data-testid="store-pod-hours-loader" data-component="StorePodHoursLoader">
        {new Array(numberOfRows).fill('').map((foobar, i) => (
          <StorePodLoader height="2rem" key={`row-${i}`} />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="store-pod-hours" data-component="StorePodHours">
      {normalDays.length === 1 && getOpenCloseBlock(mon)}
      {normalDays.length > 1 && getOpenCloseBlock((normalDays[0]), normalDays.slice(-1)[0])}
      {irregularDays.map((day) => getOpenCloseBlock(day))}
      {hoursType !== 'curbside' && getOpenCloseBlock(sun)}
    </div>
  );
};

StorePodHours.propTypes = {
  hoursType: string,
  displayMultiline: bool,
  boldLabel: bool,
  height: string
};

StorePodHours.defaultProps = {
  hoursType: '',
  displayMultiline: false,
  boldLabel: true,
  height: 'tight'
};

StorePodHours.displayName = 'StorePodHours';
